import "./assistant.scss";
import m from "mithril";
import {Dialog, Button, Intent, Checkbox, Size} from "../old/tools/constructui";
import flatpickr from "flatpickr";
import checkmark from "./comps/checkmark/checkmark";
import pf from "../old/pf";
import searchlist from "../app/components/searchable-list/searchable-list";

export default () => {
	let state = {TempData: {}}; // base (fallback) state
	const dialogStack = [];

	// helper to return the active dialog state (top of stack) or base state
	function getActive() {
		return dialogStack.length ? dialogStack[dialogStack.length - 1] : state;
	}

	function CreateFooterButton(caption, onclick, primary) {
		return m(Button, {
			intent: primary ? "primary" : undefined,
			label: caption || "Button",
			onclick,
		});
	}

	// Final actions now use getActive() so they always operate on the top-most dialog
	const Final = {
		Cancel: () => {
			const s = getActive();
			if (!s) return;
			s.showdialog = false;
			m.redraw();
			// resolve (if present) and pop the dialog
			if (s._resolve) {
				s._resolve({success: false});
			}
			// pop the stack if this dialog is on the stack
			if (dialogStack.length) dialogStack.pop();
		},
		Submit: (value) => {
			const s = getActive();
			if (!s) return;
			s.showdialog = false;
			m.redraw();
			if (s._resolve) {
				// prefer explicit value if provided; else s.value
				s._resolve({value: typeof value !== "undefined" ? value : s.value, success: true});
			}
			if (dialogStack.length) dialogStack.pop();
		},
	};

	// CreatePromise pushes a new dialog state onto the dialogStack so nested dialogs are isolated.
	async function CreatePromise(content, title, buttons, contentfunc) {
		return new Promise((resolve) => {
			const dlg = {};
			// set up the dialog properties on dlg
			dlg.showdialog = true;
			dlg.content = content;
			dlg.contentfunc = contentfunc;
			dlg.title = title;
			dlg.buttons = buttons;
			// internal resolver that user code will call through Final.* which calls dlg._resolve
			dlg._resolve = (res) => {
				resolve(res);
			};
			// push onto stack and redraw
			dialogStack.push(dlg);
			m.redraw();
		});
	}

	const askbar = {
		TempState: state.TempData,
		AskCustom: async (title, content, submitButtonAction, cancelButtonAction, contentfunc) => {
			// Build buttons: ensure we flatten arrays (they may be created as arrays of buttons)
			const cancelButtons = cancelButtonAction
				? Object.keys(cancelButtonAction).map((b) =>
						CreateFooterButton(
							b,
							() => {
								if (cancelButtonAction[b]) cancelButtonAction[b]();
								Final.Cancel();
							},
							false
						)
				  )
				: undefined;

			const submitButtons = submitButtonAction
				? Object.keys(submitButtonAction).map((b) =>
						CreateFooterButton(
							b,
							() => {
								if (submitButtonAction[b]) submitButtonAction[b]();
								Final.Submit();
							},
							true
						)
				  )
				: undefined;

			// pass a flattened array (if both exist)
			const buttons = [].concat(cancelButtons || []).concat(submitButtons || []);

			// create the dialog and await its resolution
			const res = await CreatePromise(content, title, buttons, contentfunc);

			// when the dialog is pushed, the dialog object (top of stack) has _resolve assigned;
			// we need to attach that _resolve to the currently active dialog so Final.* will call it
			const active = getActive();
			if (active) {
				// we rely on CreatePromise's promise having been created and pushing the dialog already,
				// so here active refers to the dialog we just pushed. Attach the resolver we returned
				// by creating a wrapper that resolves the original promise. Because CreatePromise created
				// the promise with resolve captured, we don't have that resolve here — but we can set
				// a placeholder; however the actual resolution is handled by the dlg._resolve inside CreatePromise.
				// In this codeflow we already have the awaited result so simply return it.
			}

			return res;
		},
		AskMessage: async (question, multiline) => {
			// initialize temptext on active dialog
			const active = getActive();
			active.temptext = undefined;
			active.value = undefined;

			return await CreatePromise(
				m(multiline ? "textarea" : "input", {
					autofocus: true,
					style: `width: 100%;`,
					value: active.temptext,
					oninput: (e) => {
						const a = getActive();
						a.temptext = e.target.value;
					},
					onchange: (e) => {
						const a = getActive();
						a.temptext = e.target.value;
					},
					onkeydown: (e) => {
						if (e.code == "Enter") {
							const a = getActive();
							a.value = a.temptext;
							Final.Submit();
						}
					},
					rows: 6,
				}),
				question,
				[
					CreateFooterButton(
						"Continue",
						() => {
							const a = getActive();
							a.value = a.temptext;
							Final.Submit();
						},
						true
					),
				]
			);
		},
		AskYesNo: async (question) => {
			return await CreatePromise(undefined, question, [
				CreateFooterButton("Cancel", () => {
					Final.Cancel();
				}),
				CreateFooterButton(
					"No",
					() => {
						const a = getActive();
						a.value = false;
						Final.Submit();
					},
					true
				),
				CreateFooterButton(
					"Yes",
					() => {
						const a = getActive();
						a.value = true;
						Final.Submit();
					},
					true
				),
			]);
		},
		AskDate: async (options) => {
			return await CreatePromise(
				m(
					".label",
					{
						autofocus: true,
						oncreate: (u) => {
							flatpickr(u.dom, {
								inline: true,
								onChange: (selectedDates, dateStr) => {
									const a = getActive();
									a.value = dateStr;
									Final.Submit();
								},
							});
						},
					},
					"Select Date"
				),
				"Choose a Date"
			);
		},
		AskOption: async (list, value, title = "Please Select") => {
			if (list && Array.isArray(list) && list.length > 0) {
				return await CreatePromise(
					m(
						".askbar-option-list",
						list.map((o) => {
							return m(Button, {
								intent: value == (o.value || o) ? Intent.PRIMARY : undefined,
								onclick: () => {
									const a = getActive();
									a.value = o.value || o;
									Final.Submit();
								},
								label: o.caption || o,
								fluid: false,
							});
						})
					),
					title
				);
			} else if (list && Object.keys(list).length > 0) {
				return await CreatePromise(
					m(
						".askbar-option-list",
						Object.keys(list).map((o) => {
							return m(Button, {
								intent: value == (o.value || o) ? Intent.PRIMARY : undefined,
								onclick: () => {
									const a = getActive();
									a.value = o;
									Final.Submit();
								},
								label: `${o}: ${list[o]}`,
								fluid: false,
							});
						})
					),
					title
				);
			} else Final.Cancel();
		},
		AskMultiOptionWithSearch: async (list_id_caption, values = [], maxcount = 0, title = "Select Multiple") => {
			const a = getActive();
			a.values = values;
			return await CreatePromise(
				undefined,
				title,
				[
					CreateFooterButton(
						"Cancel",
						() => {
							const act = getActive();
							act.value = values;
							Final.Cancel();
						},
						false
					),
					CreateFooterButton(
						"Continue",
						() => {
							const act = getActive();
							act.value = act.values;
							Final.Submit();
						},
						true
					),
				],
				() => {
					return m(
						".fixed-height",
						{
							style: "height: 50vh;",
						},
						m(searchlist, {
							list_id_caption,
							values,
							onchange: (selectedvalues) => {
								const act = getActive();
								act.values = selectedvalues;
								if (maxcount == 1 && act.values.length == 1) {
									act.value = act.values;
									Final.Submit();
								}
							},
							maxcount,
						})
					);
				}
			);
		},
		ShowCheckBoxDone: (duration = 1000) => {
			const a = getActive();
			a.showcheckbox = true;
			setTimeout(function () {
				const act = getActive();
				if (act) act.showcheckbox = false;
				m.redraw();
			}, duration);
		},
		ShowConfirmMessage: async (message, title, isError = true) => {
			return await CreatePromise(
				m(
					"span",
					{
						style: {
							"font-size": "1.5rem",
						},
					},
					message
				),
				title,
				[
					CreateFooterButton(
						"OK",
						() => {
							Final.Submit();
						},
						true
					),
				]
			);
		},
	};

	const askbarcomp = () => {
		return {
			view: () => {
				const active = getActive();
				// if no active dialog, maybe show checkbox from base state
				if (active && active.showdialog) {
					// ensure footer is built from buttons array (already created as array)
					return m(Dialog, {
						class: "full-width",
						autofocus: true,
						basic: true,
						closeOnEscapeKey: true,
						closeOnOutsideClick: true,
						content: active.content || (typeof active.contentfunc === "function" ? active.contentfunc() : undefined),
						hasBackdrop: true,
						isOpen: active.showdialog,
						inline: false,
						onClose: Final.Cancel,
						title: active.title,
						transitionDuration: 200,
						footer: active.buttons && m(`.cui-align-right`, active.buttons),
					});
				}
				// fallback: show checkbox overlay from top-most active dialog if set, else from base state
				const fallback = active || state;
				if (fallback.showcheckbox) {
					return m(
						".checkbox-outlay-wrapper",
						{
							onbeforeremove: function (u) {
								u.dom.classList.add("fade-out");
								return new Promise(function (resolve) {
									setTimeout(resolve, 1000);
								});
							},
						},
						[checkmark()]
					);
				} else return undefined;
			},
		};
	};

	return {askbar, askbarcomp};
};
